import {
    GuxFormFieldTextLike,
    GuxModal,
    GuxButtonSlot,
    GuxIcon,
    GuxInlineAlert,
} from "genesys-spark-components-react";

import { useContext, useState } from "react";

import {
    selectIsModified,
    disableSurveyForm,
    selectCanEditForm,
    selectCanDisableForm,
    // saveAiScoringSettings,
    qualityFormActions,
} from "features/qualityForm/qualityFormSlice";
import {
    selectForm,
    selectFormType,
    selectIsSurveyOrEvaluationForm,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { selectIsInCopyingViewMode } from "./copyFormSlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import strings from "./BottomBanner.strings";
import { useStrings } from "features/i18n/useStrings";
import "./BottomBanner.less";
import { ManageTemplate } from "./ManageTemplate";
import { selectIsFormValid } from "../qualityFormValidations";
import { I18nComponent } from "react/utils/I18nComponent";
import { HasAddFormPermissionsContext } from "../common/Contexts";
import {
    CopyFormButton,
    DisableFormButton,
    DiscardChangesButton,
    PublishFormButton,
    SaveButton,
} from "./BottomBannerButtons";
import embeddingService from "services/embeddingService";
import { currentUserActions } from "features/auth/currentUserSlice";
import QualityFormPermission from "enums/qualityFormPermission";

function FormDisablingModal({
    isOpened,
    setShowConfirmationPopUp,
    formName,
}: {
    isOpened: boolean;
    setShowConfirmationPopUp: React.Dispatch<React.SetStateAction<boolean>>;
    formName: string;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const placeholder = t.disable.confirm.input.placeholder({
        formName: formName.trim(),
    });
    const [confirmFormName, setConfirmFormName] = useState("");
    const [formNameMismatch, setFormNameMismatch] = useState(false);

    return (
        <GuxModal
            open={isOpened}
            onGuxdismiss={(event) => {
                event.preventDefault();
                setShowConfirmationPopUp(false);
            }}
        >
            <h1
                slot="title"
                className="disable-alert-title gux-heading-lg-semibold"
            >
                <I18nComponent
                    i18nKey="BottomBanner.disable.confirm.title"
                    substitutions={{
                        Strong: <strong>{formName}</strong>,
                    }}
                ></I18nComponent>
            </h1>
            <div slot="content" className="disable-alert-content">
                <GuxInlineAlert
                    accent="warning"
                    className="disable-alert-warning"
                >
                    <div slot="content">
                        <div className="gux-heading-xs-semibold">
                            {t.disable.confirm.warning.title}
                        </div>
                        <ul>
                            <li>
                                <I18nComponent
                                    i18nKey="BottomBanner.disable.confirm.warning.fallback"
                                    substitutions={{
                                        Strong: <strong></strong>,
                                    }}
                                ></I18nComponent>
                            </li>
                            <li>
                                <I18nComponent
                                    i18nKey="BottomBanner.disable.confirm.warning.expire"
                                    substitutions={{
                                        Strong: <strong></strong>,
                                    }}
                                ></I18nComponent>
                            </li>
                        </ul>
                    </div>
                </GuxInlineAlert>
                <GuxFormFieldTextLike labelPosition="screenreader">
                    <input
                        slot="input"
                        type="text"
                        required={true}
                        placeholder={placeholder}
                        value={confirmFormName}
                        onInput={({ currentTarget }) => {
                            setConfirmFormName(currentTarget.value);
                        }}
                    ></input>
                    <label slot="label">{placeholder}</label>
                    {formNameMismatch && (
                        <span slot="error" role="alert">
                            {t.disable.confirm.error.nameMismatch}
                        </span>
                    )}
                </GuxFormFieldTextLike>
            </div>
            <div slot="start-align-buttons">
                <GuxButtonSlot accent="primary">
                    <button
                        type="button"
                        onClick={() => {
                            if (formName.trim() === confirmFormName) {
                                setFormNameMismatch(false);
                                dispatch(disableSurveyForm());
                                setShowConfirmationPopUp(false);
                            } else {
                                setFormNameMismatch(true);
                            }
                        }}
                    >
                        {t.disable.disable}
                    </button>
                </GuxButtonSlot>
                <GuxButtonSlot>
                    <button
                        type="button"
                        onClick={() => {
                            setShowConfirmationPopUp(false);
                        }}
                    >
                        {t.disable.cancel}
                    </button>
                </GuxButtonSlot>
            </div>
        </GuxModal>
    );
}

// create a AiScoringSaveFailureModal component
function AiScoringSaveFailureModal({
    isOpened,
    setShowAiScoringFailureModal,
    removeAiScoringPermissionWithDiscardChanges,
    wasNewForm,
}: {
    isOpened: boolean;
    setShowAiScoringFailureModal: React.Dispatch<React.SetStateAction<boolean>>;
    removeAiScoringPermissionWithDiscardChanges: boolean;
    wasNewForm: boolean;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const formId = useAppSelector((state) => selectForm(state)?.id);
    const formType = useAppSelector(selectFormType);
    const discardAiScoringChanges = () => {
        setShowAiScoringFailureModal(false);
        // remove ai scoring settings
        dispatch(qualityFormActions.setAiScoringSettings());
        // remove aiScoringEditPermission if removeAiScoringPermissionWithDiscardChanges is true
        if (removeAiScoringPermissionWithDiscardChanges) {
            dispatch(
                currentUserActions.removeUserPermission({
                    permissionName:
                        QualityFormPermission.QUALITY_EVAL_FORM_AI_SCORING_EDIT,
                })
            );
        }
        if (wasNewForm) {
            embeddingService.requestNavigation(`#/${formType}Forms/${formId}`);
        } else {
            dispatch(
                qualityFormActions.setIsFormModified({ isModified: false })
            );
        }
    };

    return (
        <GuxModal
            open={isOpened}
            onGuxdismiss={(event) => {
                event.preventDefault();
                discardAiScoringChanges();
            }}
        >
            <div
                className="AiScoringFailureModalTitle"
                slot="title"
                data-testid="ai-scoring-failure-modal-title"
            >
                <GuxIcon
                    iconName="fa/hexagon-exclamation-solid"
                    screenreaderText={t.aiScoringFailure.title}
                    decorative={true}
                />
                <span className="gux-heading-lg-bold">
                    {t.aiScoringFailure.title}
                </span>
            </div>
            <div slot="content">{t.aiScoringFailure.content}</div>
            {
                // hide Retry Save button temporarily
                /* <div slot="left-align-buttons">
                <GuxButtonSlot accent="primary">
                    <button
                        onClick={() => {
                            setShowAiScoringFailureModal(false);
                            // resend the request to AI Scoring configuration
                            dispatch(saveAiScoringSettings()).then((e) => {
                                if ("error" in e) {
                                    setShowAiScoringFailureModal(true);
                                }
                                // TODO: send toast message?
                            });
                        }}
                    >
                        {t.aiScoringFailure.retry}
                    </button>
                </GuxButtonSlot>
            </div> */
            }
            <div slot="end-align-buttons">
                <GuxButtonSlot accent="secondary">
                    <button
                        data-testid="discard-button"
                        onClick={discardAiScoringChanges}
                    >
                        {t.aiScoringFailure.discard}
                    </button>
                </GuxButtonSlot>
            </div>
        </GuxModal>
    );
}

export function BottomBanner() {
    const formType = useAppSelector(selectFormType);
    const canDisableForm = useAppSelector(selectCanDisableForm);
    const [inProgress, setInProgress] = useState(false);
    const [showConfirmationPopUp, setShowConfirmationPopUp] = useState(false);
    const [showAiScoringFailureModal, setShowAiScoringFailureModal] =
        useState(false);
    const [
        removeAiScoringPermissionWithDiscardChanges,
        setRemoveAiScoringPermissionWithDiscardChanges,
    ] = useState(false);
    const [wasNewForm, setWasNewForm] = useState(false);
    const isFormValid = useAppSelector((state) => selectIsFormValid(state));
    const isModified = useAppSelector((state) => selectIsModified(state));
    const formName = useAppSelector((state) => selectForm(state)?.name ?? "");
    const formId = useAppSelector((state) => selectForm(state)?.id);
    const isExistingForm = formId !== "new" && formId !== undefined;

    const canEditForm = useAppSelector((state) => {
        return selectCanEditForm(state);
    });
    const { hasAddFormPermissions } = useContext(HasAddFormPermissionsContext);

    const isCopyFormButtonVisible = useAppSelector((state) => {
        return !selectIsInCopyingViewMode(state);
    });
    const isInCopyingViewMode = !isCopyFormButtonVisible;

    const isCopyFormButtonDisabled =
        !isExistingForm || isModified || !isFormValid;
    const isSurveyOrEvaluation = useAppSelector(selectIsSurveyOrEvaluationForm);

    const saveButton = (
        <SaveButton
            disabled={!isModified || inProgress || !isFormValid}
            setInProgress={setInProgress}
            formType={formType}
            isInCopyingViewMode={isInCopyingViewMode}
            setShowAiScoringFailureModal={setShowAiScoringFailureModal}
            setRemoveAiScoringPermissionWithDiscardChanges={
                setRemoveAiScoringPermissionWithDiscardChanges
            }
            setWasNewForm={setWasNewForm}
        />
    );
    const publishButton = (
        <PublishFormButton
            disabled={
                !isExistingForm || isModified || inProgress || !isFormValid
            }
            setInProgress={setInProgress}
            formType={formType}
        />
    );
    const copyOrDiscardButton =
        hasAddFormPermissions &&
        (isCopyFormButtonVisible ? (
            <CopyFormButton disabled={isCopyFormButtonDisabled} />
        ) : (
            <DiscardChangesButton />
        ));
    const aiScoringFailureModal = showAiScoringFailureModal && (
        <AiScoringSaveFailureModal
            isOpened={showAiScoringFailureModal}
            setShowAiScoringFailureModal={setShowAiScoringFailureModal}
            wasNewForm={wasNewForm}
            removeAiScoringPermissionWithDiscardChanges={
                removeAiScoringPermissionWithDiscardChanges
            }
        />
    );

    return (
        <div
            className={`BottomBanner${
                isSurveyOrEvaluation ? " surveyOrEvaluation" : ""
            }`}
        >
            {canEditForm ? (
                <div className="form-controls-unpublished">
                    {isSurveyOrEvaluation ? (
                        <>
                            <div>
                                {saveButton}
                                {publishButton}
                                {copyOrDiscardButton}
                                {aiScoringFailureModal}
                            </div>
                            <ManageTemplate />
                        </>
                    ) : (
                        <>
                            <ManageTemplate />
                            <div>
                                {copyOrDiscardButton}
                                {publishButton}
                                {saveButton}
                                {aiScoringFailureModal}
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="form-controls-published">
                    {hasAddFormPermissions && (
                        <CopyFormButton disabled={isCopyFormButtonDisabled} />
                    )}
                    {canDisableForm && (
                        <>
                            <DisableFormButton
                                setShowConfirmationPopUp={
                                    setShowConfirmationPopUp
                                }
                            />
                            {showConfirmationPopUp && (
                                <FormDisablingModal
                                    isOpened={showConfirmationPopUp}
                                    setShowConfirmationPopUp={
                                        setShowConfirmationPopUp
                                    }
                                    formName={formName}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
